import React, { useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { useIsomorphicLayoutEffect } from 'react-use';
import { useTransform, motion, useSpring } from 'framer-motion';
import { dry } from 'constants/springs';
import media from 'styles/media';
import { propsToDom, clamp } from 'core/helpers';
import useOffsetSize from 'hooks/useOffsetSize';
import { ContainerGrid } from 'components/Container';
import { Panel } from 'components/Panel';
import { Image } from 'components/Image';
import { useGlobalContext } from 'store/GlobalProvider';
import { useViewportScroll } from 'hooks/useViewportScroll';

const radians = (degrees) => degrees * (Math.PI / 180);

const GalleryScrollItemComponent = ({
  progress: mainProgress,
  image,
  index,
  stickyHeight: height,
  wh,
  length,
  ...others
}) => {
  const { isMobile } = useGlobalContext();
  const progress = useTransform(mainProgress, (p) => (p - index * wh) / wh);
  const clamped = useTransform(progress, [0, 1], [0, 1]);

  const direction = index % 2 === 1 ? 1 : -1;
  const angle = isMobile ? 0 : 10;
  const distY = (wh - height) / 2 + height + Math.sin(radians(angle)) * height;
  const distX = isMobile ? 0 : 150 * direction;
  const y = useTransform(progress, [-length, 0, 1], [20 + 10 / index, 0, -distY]);
  const x = useTransform(clamped, [0, 1], [0, distX]);
  const rotate = useTransform(clamped, [0, 1], [0, angle * direction]);
  const scale = useTransform(progress, [-length, 0], [0.8, 1]);

  return (
    <Image
      {...propsToDom(others)}
      {...(isMobile ? image.thumbnails.mobile : image)}
      key={index}
      Component={motion.div}
      style={{
        y,
        x,
        rotate,
        scale,
        zIndex: length - index,
        transformOrigin: '50% 100%',
      }}
      transformTemplate={({ x, y, rotate, scale }) =>
        `translate(-50%, -50%) scale(${scale}) translate3d(${x}, ${y}, 0) rotate(${rotate})`
      }
    />
  );
};

const GalleryScrollComponent = ({ primary, items, slice_type, next_slice_type, ...others }) => {
  const { wh, ww, isLoaded } = useGlobalContext();
  const scrollY = useViewportScroll();
  const scroll = useSpring(scrollY, dry);
  const $sticky = useRef(null);
  const [{ top, height }, $container] = useOffsetSize();
  const [stickyHeight, setStickyHeight] = useState(null);
  const { background } = primary;
  const images = useMemo(() => items.filter(({ image }) => !!image?.url), [items]);
  const stickyTop = wh > 0 && stickyHeight > 0 && wh / 2 - stickyHeight / 2;
  const progress = useTransform(scroll, (scroll) =>
    clamp(scroll - top + stickyTop, 0, height - wh)
  );

  useIsomorphicLayoutEffect(() => {
    if ($sticky.current) {
      const height = Math.max(...[...$sticky.current.children].map(($node) => $node.offsetHeight));

      setStickyHeight(height);
    }
  }, [wh, ww, isLoaded]);

  return (
    <Panel
      {...propsToDom(others)}
      id={slice_type}
      nextId={next_slice_type}
      background={background}
      style={{
        '--length': images.length,
        '--sticky-height': stickyHeight ? `${stickyHeight}px` : null,
      }}
    >
      <ContainerGrid>
        <Container ref={$container}>
          <Sticky ref={$sticky} style={{ top: stickyTop }}>
            {images.map(({ image }, index) => (
              <GalleryScrollItem
                key={index}
                image={image}
                index={index}
                length={images.length}
                progress={progress}
                stickyHeight={stickyHeight}
                wh={wh}
              />
            ))}
          </Sticky>
        </Container>
      </ContainerGrid>
    </Panel>
  );
};

const GalleryScrollItem = styled(GalleryScrollItemComponent)``;

const Sticky = styled.div`
  position: sticky;
  top: 0;
  width: 100%;
  height: var(--sticky-height, auto);

  ${Image} {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100%;
    transform: translate(-50%, -50%);
    border-radius: 8px;
    overflow: hidden;
  }
`;

const Container = styled.div`
  grid-column: 2 / -2;
  height: calc(var(--viewport-height, 100vh) * var(--length, 1));

  ${media.mobile`
    grid-column: 1 / -1;
  `}
`;

export const GalleryScroll = styled(GalleryScrollComponent)``;

export const query = graphql`
  fragment SliceGalleryScrollHome on PrismicHomeDataBodyGalleryScroll {
    id
    slice_type
    primary {
      background
    }
    items {
      image {
        url
        alt
        dimensions {
          width
          height
        }
        thumbnails {
          mobile {
            url
            alt
            dimensions {
              width
              height
            }
          }
        }
      }
    }
  }

  fragment SliceGalleryScrollPage on PrismicPageDataBodyGalleryScroll {
    id
    slice_type
    primary {
      background
    }
    items {
      image {
        url
        alt
        dimensions {
          width
          height
        }
        thumbnails {
          mobile {
            url
            alt
            dimensions {
              width
              height
            }
          }
        }
      }
    }
  }
`;
